import type { Folder } from '@deepcloud/api-deep-box'
import type { TaskUploadNode } from '../types'

export type FolderWithMeta = Folder & {
  _folderId: string
  children?: FolderWithMeta[]
}

export function useFolderTreeBuilder() {
  /**
   * Builds the nested Folder[] payload for the API from the flat folders map,
   * starting from the given parentId ('ROOT' means top-level).
   * Also returns a parallel structure mapping each Folder node back to its
   * taskUpload folderId so we can reconcile the response afterward.
   */
  function buildApiFolderTree(
    folders: Record<string, TaskUploadNode>,
    parentId: string = 'ROOT',
  ): FolderWithMeta[] {
    return Object.entries(folders)
      .filter(([, node]) => (node.data?.parent ?? 'ROOT') === parentId)
      .map(([folderId, node]): FolderWithMeta => ({
        _folderId: folderId,
        name: node.data?.name ?? '',
        children: buildApiFolderTree(folders, folderId),
      }))
  }

  /**
   * Strip the internal _folderId metadata before sending to the API.
   */
  function toApiFolders(metaFolders: FolderWithMeta[]): Folder[] {
    return metaFolders.map(({ children, ...rest }) => ({
      ...(rest as Omit<FolderWithMeta, '_folderId' | 'children'>),
      ...(children?.length ? { children: toApiFolders(children) } : {}),
    }))
  }

  return { buildApiFolderTree, toApiFolders }
}
